import { useMemo, useState } from "react";
import { Search } from "lucide-react";

import type { TrackedSymbol } from "../api/types";
import { fuzzyMatch } from "../lib/fuzzy";
import { SignalPill, signalVariant } from "./SignalPill";

interface WatchlistProps {
  trackedSymbols: TrackedSymbol[];
  recentSymbols: string[];
  selectedSymbol: string | null;
  onSelect: (symbol: string) => void;
  onHover?: (symbol: string) => void;
}

export function Watchlist({
  trackedSymbols,
  recentSymbols,
  selectedSymbol,
  onSelect,
  onHover,
}: WatchlistProps) {
  const [filter, setFilter] = useState("");

  const filtered = useMemo(() => {
    const q = filter.trim();
    if (!q) {
      return trackedSymbols;
    }
    return trackedSymbols
      .map((item) => {
        const haystack = item.company_name
          ? `${item.symbol} ${item.company_name}`
          : item.symbol;
        const score = fuzzyMatch(q, haystack);
        return score === null ? null : { item, score };
      })
      .filter((entry): entry is { item: TrackedSymbol; score: number } => entry !== null)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.item);
  }, [filter, trackedSymbols]);

  return (
    <section className="watchlist" aria-label="Watchlist">
      <div className="watchlist-search">
        <Search className="watchlist-search-icon" aria-hidden />
        <input
          className="watchlist-input"
          placeholder="Filter symbols…"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          aria-label="Filter watchlist"
        />
      </div>

      {recentSymbols.length > 0 && (
        <div className="watchlist-recents">
          <p className="rail-label">Recent</p>
          <div className="recent-chips">
            {recentSymbols.slice(0, 6).map((symbol) => (
              <button
                key={symbol}
                type="button"
                className={`recent-chip${symbol === selectedSymbol ? " is-active" : ""}`}
                onClick={() => onSelect(symbol)}
                onMouseEnter={() => onHover?.(symbol)}
              >
                {symbol}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="watchlist-body">
        <p className="rail-label watchlist-count">{filtered.length} symbols</p>
        <ul className="watchlist-list">
          {filtered.length === 0 ? (
            <li className="empty-state watchlist-empty">No symbols match “{filter.trim()}”.</li>
          ) : (
            filtered.map((item) => {
              const isActive = item.symbol === selectedSymbol;
              return (
                <li key={item.symbol}>
                  <button
                    type="button"
                    className={`watchlist-row${isActive ? " is-active" : ""}`}
                    onClick={() => onSelect(item.symbol)}
                    onMouseEnter={() => onHover?.(item.symbol)}
                    aria-current={isActive ? "true" : undefined}
                  >
                    <span
                      className={`watchlist-tick tick-${signalVariant(item.signal)}`}
                      aria-hidden
                    />
                    <span className="watchlist-symbol">{item.symbol}</span>
                    <span className="watchlist-company">{item.company_name || "—"}</span>
                    <SignalPill signal={item.signal} size="sm" />
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </section>
  );
}
